import { useSplitTreatments } from '@splitsoftware/splitio-react'
import { useContext, useEffect, useMemo } from 'react'

import {
  FeatureFlipAttributesContext,
  IsImpersonationContext,
} from './FeaturesWrapper'
import {
  clearCachedTreatment,
  getCachedTreatment,
  refreshCacheTTL,
  setCachedTreatment,
  SPLIT_STORAGE_PREFIX,
} from './splitCache'

/** Fresh cache TTL - cache is considered "fresh" for 30 seconds after mutation */
const FRESH_CACHE_TTL_MS = 30_000

/**
 * Get fresh cache entry if it exists and is < 30 seconds old.
 * Used to bridge the Split.io CDN propagation delay (2-5s) after mutations.
 */
function getFreshCache(
  name: string,
): { treatment: string; config: string | null } | null {
  try {
    const cached = localStorage.getItem(`${SPLIT_STORAGE_PREFIX}${name}`)
    if (!cached) return null
    const parsed = JSON.parse(cached) as {
      treatment: string
      config: string | null
      cachedAt?: number
    }
    if (typeof parsed.cachedAt !== 'number') return null
    const age = Date.now() - parsed.cachedAt
    if (age > FRESH_CACHE_TTL_MS) return null
    return { treatment: parsed.treatment, config: parsed.config }
  } catch {
    return null
  }
}

/**
 * Traffic type for Split.io evaluation.
 * - `'organization'` — evaluate against the organization ID (default)
 * - `'user'` — evaluate against a user/account ID (requires `splitKey`)
 *
 * The traffic type must match the split flag's configuration in Split.io.
 */
export type SplitTrafficType = 'organization' | 'user'

export interface SplitOptions {
  /** Additional attributes to pass to Split.io for targeting */
  attributes?: Record<string, unknown>
  /** Key to evaluate against (e.g. account ID for user-based splits).
   *  When provided, the SDK creates a client for this key and fetches its segments. */
  splitKey?: SplitIO.SplitKey
  /** Traffic type for the splitKey. Required when splitKey
   *  belongs to a different traffic type than the default 'organization'. */
  trafficType?: SplitTrafficType
  /** Skip the localStorage treatment cache (read and write) for this
   *  evaluation. The cache is keyed by flag name only, so it cannot tell apart
   *  evaluations that target on per-call `attributes`: a value cached for one
   *  attribute set would apply to another before the SDK is ready. Manual
   *  overrides still apply. */
  skipCache?: boolean
}

/**
 * Hook to get a Split.io feature flag treatment.
 *
 * Supports localStorage caching to prevent UI flickering on page loads.
 * Manual overrides can be set via `localStorage.setItem('split.io/flag-name', 'treatment')`.
 *
 * @template T - Type of the parsed config JSON (defaults to empty object)
 * @param name - The feature flag name
 * @param options - Optional configuration for targeting and key override
 * @returns Object containing treatment value, ready state, and parsed config
 *
 * @example
 * ```tsx
 * const { split, isReady, config } = useSplit<{ limit: number }>('my-feature')
 *
 * if (!isReady) return <Loading />
 * if (split.treatment === 'on') return <NewFeature limit={config.limit} />
 * return <OldFeature />
 * ```
 */
export function useSplit<T = Record<string, never>>(
  name: string,
  options: SplitOptions = {},
): {
  isReady: boolean
  isReadyFromCache: boolean
  isTimedout: boolean
  split: SplitIO.TreatmentWithConfig
  config: T
  client: SplitIO.IBrowserClient | null
} {
  const { attributes = {}, splitKey, trafficType, skipCache = false } = options

  const isImpersonation = useContext(IsImpersonationContext)

  const featureFlipAttributes = useContext(FeatureFlipAttributesContext)
  const featureFlipAttributesReady = featureFlipAttributes !== null
  const combinedAttributes = {
    ...featureFlipAttributes,
    ...attributes,
  }

  const { treatments, isReady, isTimedout, client } = useSplitTreatments({
    names: [name],
    attributes: combinedAttributes,
    splitKey,
    trafficType,
  })

  const sdkTreatment = treatments[name] || {
    treatment: 'control',
    config: null,
  }
  const treatmentValue = sdkTreatment.treatment
  const configValue = sdkTreatment.config

  // Cache treatment when SDK is ready (using primitive deps to avoid unnecessary runs)
  // IMPORTANT: Don't overwrite cache if it's "fresh" (< 30 seconds old)
  // This allows mutation results to take priority during Split.io CDN propagation (2-5s window)
  useEffect(() => {
    if (isImpersonation || skipCache) return
    if (isReady && treatmentValue !== 'control') {
      if (!getFreshCache(name)) {
        setCachedTreatment(name, treatmentValue, configValue)
      }
    }
  }, [name, isReady, treatmentValue, configValue, isImpersonation, skipCache])

  // Check for fresh cache (highest priority after manual overrides)
  // This takes priority over SDK during Split.io CDN propagation (2-5s window)
  // NOTE: Intentionally NOT memoized - Date.now() must re-evaluate each render
  // to properly expire after 30 seconds
  const freshCache = isImpersonation || skipCache ? null : getFreshCache(name)

  // Always check for overrides (they take priority regardless of SDK state)
  // With `skipCache`, only manual overrides are read.
  const cached = useMemo(() => {
    if (isImpersonation) return null
    const stored = getCachedTreatment(name)
    return skipCache && stored?.type === 'cache' ? null : stored
  }, [name, isImpersonation, skipCache])

  // Handle expired cache (must be before early return to satisfy Rules of Hooks)
  const isExpiredCache = cached?.type === 'cache' && cached.expired
  useEffect(() => {
    if (isExpiredCache) {
      clearCachedTreatment(name)
    }
  }, [name, isExpiredCache])

  // Determine if we're using a valid (non-expired) cache (only when SDK not ready)
  const usingCache = cached?.type === 'cache' && !cached.expired && !isReady

  // Refresh TTL when actually using the cache
  useEffect(() => {
    if (usingCache) {
      refreshCacheTTL(name)
    }
  }, [name, usingCache])

  // Manual override takes priority - checked regardless of SDK state
  if (cached?.type === 'override') {
    // Only show warning for Buffer team members
    const isBufferTeam = getCachedTreatment('is-buffer-team')
    const isBufferTeamMember =
      (isBufferTeam?.type === 'override' && isBufferTeam.treatment === 'on') ||
      (isBufferTeam?.type === 'cache' &&
        isBufferTeam.treatment.treatment === 'on')

    if (isBufferTeamMember) {
      // eslint-disable-next-line no-console
      console.log(
        '⚠️ %cSPLIT.IO OVERRIDE ACTIVE:%c Using localStorage split for ' + name,
        'background: #ffcd85; color: #000; font-weight: bold;',
        'background: transparent; color: #000;',
      )
    }
    return {
      isReady: true,
      isReadyFromCache: true,
      isTimedout: false,
      split: { treatment: cached.treatment, config: null },
      config: {} as T,
      client,
    }
  }

  // Fresh cache takes priority over SDK (prevents showing stale data during propagation)
  if (freshCache !== null) {
    let freshConfig: T = {} as T
    if (freshCache.config) {
      try {
        freshConfig = JSON.parse(freshCache.config) as T
      } catch {
        // eslint-disable-next-line no-console
        console.warn(`Failed to parse config for split "${name}"`)
      }
    }
    return {
      isReady: true,
      isReadyFromCache: true,
      isTimedout: false,
      split: { treatment: freshCache.treatment, config: freshCache.config },
      config: freshConfig,
      client,
    }
  }

  const split = usingCache ? cached.treatment : sdkTreatment

  let config: T = {} as T
  if (split.config) {
    try {
      config = JSON.parse(split.config) as T
    } catch {
      // eslint-disable-next-line no-console
      console.warn(`Failed to parse config for split "${name}"`)
    }
  }

  return {
    isReady: usingCache || (featureFlipAttributesReady && isReady),
    isReadyFromCache: usingCache,
    isTimedout,
    split,
    config,
    client,
  }
}

/**
 * Hook to get multiple Split.io feature flag treatments at once.
 *
 * Use this when you need to check several flags simultaneously to reduce SDK calls.
 * Note: This hook does not support localStorage caching.
 *
 * @param names - Array of feature flag names
 * @param options - Optional configuration for targeting and key override
 * @returns Object containing treatments map and ready state
 *
 * @example
 * ```tsx
 * const { splits, isReady } = useSplits(['feature-a', 'feature-b'])
 *
 * if (!isReady) return <Loading />
 * const showFeatureA = splits['feature-a']?.treatment === 'on'
 * const showFeatureB = splits['feature-b']?.treatment === 'on'
 * ```
 */
export function useSplits(
  names: string[],
  options: SplitOptions = {},
): {
  splits: Record<string, SplitIO.TreatmentWithConfig>
  isReady: boolean
  isTimedout: boolean
} {
  const { attributes = {}, splitKey, trafficType } = options

  const featureFlipAttributes = useContext(FeatureFlipAttributesContext)
  const featureFlipAttributesReady = featureFlipAttributes !== null
  const combinedAttributes = { ...featureFlipAttributes, ...attributes }
  const { treatments, isReady, isReadyFromCache, isTimedout } =
    useSplitTreatments({
      names,
      attributes: combinedAttributes,
      splitKey,
      trafficType,
    })

  return {
    splits: treatments,
    isReady: featureFlipAttributesReady && (isReady || isReadyFromCache),
    isTimedout,
  }
}
